import { Star, TrendingUp, CalendarCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { InstantBookBadge } from "@/components/cars/InstantBookBadge";

export type SocialProofVariant = "stack" | "inline";

export interface SocialProofBadgesProps {
  rating?: number | null;
  reviewCount?: number | null;
  instantBook?: boolean;
  dateListed?: string | null;
  variant?: SocialProofVariant;
  ratingDecimals?: 1 | 2;
  onRatingClick?: () => void;
  className?: string;
  showPopular?: boolean;
  showNewListing?: boolean;
}

function hasRating(rating?: number | null, reviewCount?: number | null): rating is number {
  return typeof rating === "number" && Number.isFinite(rating) && rating >= 1 && rating <= 5 &&
    typeof reviewCount === "number" && Number.isInteger(reviewCount) && reviewCount > 0;
}

function pluralizeReviews(count: number): string {
  return count === 1 ? "review" : "reviews";
}

function isNewListing(dateListed?: string | null): boolean {
  if (!dateListed) return false;
  const days = Math.floor((Date.now() - new Date(dateListed).getTime()) / (1000 * 60 * 60 * 24));
  return days <= 30;
}

export function SocialProofBadges({
  rating,
  reviewCount,
  instantBook,
  dateListed,
  variant = "stack",
  ratingDecimals = 1,
  onRatingClick,
  className,
  showPopular = true,
  showNewListing = true,
}: SocialProofBadgesProps) {
  const { t } = useTranslation();
  const ratingShown = hasRating(rating, reviewCount);
  const popularShown = showPopular && reviewCount != null && reviewCount >= 5;
  const newListingShown = showNewListing && isNewListing(dateListed);

  if (!ratingShown && !instantBook && !popularShown && !newListingShown) {
    return null;
  }

  if (variant === "inline") {
    const items: React.ReactNode[] = [];
    if (ratingShown) {
      const RatingTag = onRatingClick ? "button" : "span";
      items.push(
        <RatingTag
          key="rating"
          {...(onRatingClick
            ? { onClick: onRatingClick, type: "button" as const, className: "flex items-center gap-1 underline" }
            : { className: "flex items-center gap-1" })}
        >
          <Star className="h-3.5 w-3.5 fill-current text-brand" />
          <span className="font-medium text-foreground">{rating!.toFixed(ratingDecimals)}</span>
          <span className="text-muted-foreground">
            ({reviewCount} {t(`common.${pluralizeReviews(reviewCount!)}`)})
          </span>
        </RatingTag>,
      );
    }
    return (
      <div className={cn("flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground", className)}>
        {instantBook && <InstantBookBadge />}
        {items.map((node, i) => (
          <span key={i} className="flex items-center gap-2">
            {i > 0 && <span aria-hidden className="text-muted-foreground">·</span>}
            {node}
          </span>
        ))}
      </div>
    );
  }

  return (
    <div className={cn("space-y-2", className)}>
      {ratingShown && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Star className="h-3.5 w-3.5 fill-current text-brand" />
          <span className="font-medium text-foreground">{rating!.toFixed(ratingDecimals)}</span>
          <span aria-hidden>·</span>
          <span>
            {reviewCount} {t(`common.${pluralizeReviews(reviewCount!)}`)}
          </span>
        </div>
      )}
      {instantBook && (
        <div className="flex">
          <InstantBookBadge />
        </div>
      )}
      {popularShown && (
        <div className="flex items-center gap-1.5 text-xs">
          <TrendingUp className="h-3.5 w-3.5 text-rose-500" />
          <span className="font-medium uppercase tracking-wide text-rose-600">{t("carCard.social_proof.popular")}</span>
          <span className="uppercase tracking-wide text-muted-foreground">{t("carCard.social_proof.popular_suffix")}</span>
        </div>
      )}
      {newListingShown && (
        <div className="flex items-center gap-1.5 text-xs">
          <CalendarCheck className="h-3.5 w-3.5 text-blue-500" />
          <span className="font-medium uppercase tracking-wide text-blue-600">{t("carCard.social_proof.new_listing")}</span>
          <span className="uppercase tracking-wide text-muted-foreground">{t("carCard.social_proof.new_listing_suffix")}</span>
        </div>
      )}
    </div>
  );
}
